/**
 * DashboardCreator Component
 * Modal for creating and editing analytics dashboards with graphs
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import LoopIcon from "@mui/icons-material/Loop";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,

  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AnalyticsDashboardDto,
  AnalyticsGraphDto,
  AnalyticsDashboardScopeFilterDto,
  AnalyticsGraphCreateDto,
  AnalyticsGraphFilterDto,
  GraphFilterUI,
  GraphType,
  DataSourceEntity,
  AggregationType,
  ScopeFilterType,
  FilterOperator,
  DateGranularity,
  FieldTransform,
  SecondaryDimensionType,
  PREDEFINED_FIELDS,
  GRAPH_TYPES,
  AGGREGATION_TYPES,
  SCOPE_FILTER_TYPES,
  FILTER_OPERATORS,
  DATE_GRANULARITIES,
  FIELD_TRANSFORMS,
  SECONDARY_DIMENSION_TYPES,
  DATE_FIELDS_BY_SOURCE,
  DATA_SOURCE_LABELS,
  ChallengeFormFieldSelection,
  getGraphTypeLabel,
  getAggregationTypeLabel,
  getDataSourceLabel,
  getScopeFilterTypeLabel,
  toCamelCase,
} from "@/api/types/dtos/analytics/AnalyticsDto";
import { useScopeFilterOptions, useAvailableFields } from "@/api/hooks/analytics/useAnalytics";
import { ChallengeFormFieldSelectorModal } from "./ChallengeFormFieldSelectorModal";
import { useOrganization } from "@/contexts/OrganizationContext";
import { parseFieldKey } from "../utils/fieldParser";

export interface DashboardSaveData {
  name: string;
  description: string;
  scopeFilters: AnalyticsDashboardScopeFilterDto[];
  graphs: (AnalyticsGraphCreateDto & { id?: string })[];
}

interface DashboardCreatorProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: DashboardSaveData) => void;
  onDuplicate?: (dashboardId: string) => void;
  dashboard?: AnalyticsDashboardDto;
  isLoading?: boolean;
}

interface GraphFormData {
  name: string;
  description: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping
  secondaryDimensionType: SecondaryDimensionType;
  secondaryDimensionField: string;
  secondaryDimensionGranularity: DateGranularity;
  // Field transformation
  fieldTransform: FieldTransform;
  filters: GraphFilterUI[];
}

const DEFAULT_GRAPH_FORM: GraphFormData = {
  name: "",
  description: "",
  graphType: "BAR",
  dataSourceEntity: "FARM",
  dataSourceField: "name",
  aggregationType: "COUNT",
  // Secondary dimension defaults
  secondaryDimensionType: "NONE",
  secondaryDimensionField: "",
  secondaryDimensionGranularity: "MONTH",
  // Field transform defaults
  fieldTransform: "NONE",
  filters: [],
};

// Conversion functions for filter formats
const convertFiltersForBackend = (filters: GraphFilterUI[]): AnalyticsGraphFilterDto[] => {
  return filters.map((filter) => {
    const { filterField, operator, values } = filter;
    if (operator === "BETWEEN") {
      return {
        filterField,
        filterOperator: operator,
        filterValue: values[0] || "",
        filterValue2: values[1] || "",
      };
    } else if (operator === "IN") {
      return {
        filterField,
        filterOperator: operator,
        filterValue: JSON.stringify(values),
      };
    } else {
      // EQUALS, NOT_EQUALS, GREATER_THAN, LESS_THAN
      return {
        filterField,
        filterOperator: operator,
        filterValue: values[0] || "",
      };
    }
  });
};

const convertFiltersFromBackend = (backendFilters: AnalyticsGraphFilterDto[]): GraphFilterUI[] => {
  const filtersByField = new Map<string, GraphFilterUI>();

  for (const bf of backendFilters) {
    if (bf.filterOperator === "IN") {
      const values = JSON.parse(bf.filterValue || "[]");
      filtersByField.set(bf.filterField, {
        filterField: bf.filterField,
        operator: "IN",
        values,
      });
    } else if (bf.filterOperator === "BETWEEN") {
      filtersByField.set(bf.filterField, {
        filterField: bf.filterField,
        operator: "BETWEEN",
        values: [bf.filterValue || "", bf.filterValue2 || ""],
      });
    } else {
      // EQUALS, NOT_EQUALS, GREATER_THAN, LESS_THAN
      const existing = filtersByField.get(bf.filterField);
      if (existing && existing.operator === bf.filterOperator) {
        existing.values.push(bf.filterValue || "");
      } else {
        filtersByField.set(bf.filterField, {
          filterField: bf.filterField,
          operator: bf.filterOperator as FilterOperator,
          values: [bf.filterValue || ""],
        });
      }
    }
  }

  return Array.from(filtersByField.values());
};

// Sortable graph item component for drag-and-drop reordering
interface SortableGraphItemProps {
  graph: AnalyticsGraphDto;
  index: number;
  onEdit: (graph: AnalyticsGraphDto) => void;
  onDuplicate: (graph: AnalyticsGraphDto) => void;
  onDelete: (graphId: string) => void;
  getFieldDisplayLabel: (graph: AnalyticsGraphDto) => string;
  getGraphFilterLabel: (filter: AnalyticsGraphFilterDto) => string;
  convertFiltersFromBackend: (filters: AnalyticsGraphFilterDto[]) => GraphFilterUI[];
}

const SortableGraphItem: React.FC<SortableGraphItemProps> = ({
  graph,
  index,
  onEdit,
  onDuplicate,
  onDelete,
  getFieldDisplayLabel,
  getGraphFilterLabel,

}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: graph.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Accordion
        defaultExpanded={index === 0}
        sx={{
          opacity: isDragging ? 0.8 : 1,
          border: "1px solid var(--border-color)",
          borderRadius: "var(--card-border-radius) !important",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "box-shadow 150ms ease, border-color 150ms ease",
          "&:before": { display: "none" },
          "&:hover": {
            borderColor: "var(--primary)",
            boxShadow: "0 2px 8px rgba(11, 15, 40, 0.08)",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "var(--text-secondary)" }} />}
          sx={{
            backgroundColor: "var(--tertiary-light)",
            borderBottom: "1px solid var(--border-color)",
            "&:hover": {
              backgroundColor: "var(--tertiary)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", pr: 2 }}>
            {/* Drag handle */}
            <Box
              {...attributes}
              {...listeners}
              sx={{ cursor: "grab", mr: 1, display: "flex", alignItems: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              <DragHandleIcon fontSize="small" sx={{ color: "var(--text-secondary)" }} />
            </Box>
            <Typography sx={{ flex: 1, color: "var(--text-primary)", fontWeight: 500 }}>
              <Chip
                label={getGraphTypeLabel(graph.graphType)}
                size="small"
                sx={{
                  mr: 1,
                  borderRadius: "10px",
                  backgroundColor: "var(--secondary)",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              />
              {graph.name}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Edit graph clicked:", graph.name, graph);
                  onEdit(graph);
                }}
                sx={{
                  color: "var(--text-secondary)",
                  "&:hover": {
                    color: "var(--primary)",
                    backgroundColor: "var(--tertiary)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(graph);
                }}
                sx={{
                  color: "var(--text-secondary)",
                  "&:hover": {
                    color: "var(--primary)",
                    backgroundColor: "var(--tertiary)",
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(graph.id);
                }}
                sx={{
                  color: "var(--error)",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "var(--background-paper)" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
              >
                Fuente de Datos
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--text-primary)", fontWeight: 700 }}>
                {getDataSourceLabel(graph.dataSourceEntity)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
              >
                Campo
              </Typography>
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-all", color: "var(--text-primary)", fontWeight: 700 }}
              >
                {getFieldDisplayLabel(graph)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
              >
                Agregación
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--text-primary)", fontWeight: 700 }}>
                {getAggregationTypeLabel(graph.aggregationType)}
              </Typography>
            </Grid>
            {/* Show FOR_EACH contexts if present */}
            {graph.dataSourceEntity === "CHALLENGE_FORM" &&
              (() => {
                const parsedField = parseFieldKey(graph.dataSourceField);
                return parsedField?.forEachValues && parsedField.forEachValues.length > 0;
              })() && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LoopIcon fontSize="inherit" />
                      Contextos FOR_EACH
                    </Box>
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {parseFieldKey(graph.dataSourceField)?.forEachValues?.map((ctx) => (
                      <Chip
                        key={ctx}
                        label={toCamelCase(ctx)}
                        size="small"
                        sx={{
                          borderRadius: "10px",
                          border: "1px solid var(--primary)",
                          color: "var(--primary)",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            {graph.description && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
                >
                  Descripción
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--text-primary)", fontWeight: 700 }}>
                  {graph.description}
                </Typography>
              </Grid>
            )}
            {(graph.filters || []) && (graph.filters || []).length > 0 && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ color: "var(--text-secondary)", fontWeight: 500 }}
                >
                  Filtros
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {(graph.filters || []).map((filter, index) => (
                    <Chip
                      key={index}
                      label={getGraphFilterLabel(filter)}
                      size="small"
                      sx={{
                        borderRadius: "10px",
                        border: "1px solid var(--border-color)",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

// Filter Card Component
interface FilterCardProps {
  filter: GraphFilterUI;
  fieldLabel: string;
  fieldType?: string;
  enumValues?: string[];
  dataSourceEntity?: DataSourceEntity;
  isAdvancedMode: boolean;
  availableFields: Array<{
    key: string;
    label: string;
    type?: string;
    enumValues?: string[];
    dataSourceEntity?: DataSourceEntity;
  }>;
  onUpdateOperator: (operator: FilterOperator) => void;
  onAddValue: (value: string) => void;
  onRemoveValue: (value: string) => void;
  onRemove: () => void;
}

const FilterCard: React.FC<FilterCardProps> = ({
  filter,
  fieldLabel,

  enumValues,
  dataSourceEntity,
  isAdvancedMode,

  onUpdateOperator,
  onAddValue,
  onRemoveValue,
  onRemove,
}) => {
  const [showValueInput, setShowValueInput] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [tempValue2, setTempValue2] = useState("");

  const handleAddValue = () => {
    if (filter.operator === "BETWEEN") {
      if (tempValue && tempValue2) {
        onAddValue(tempValue);
        onAddValue(tempValue2);
        setTempValue("");
        setTempValue2("");
        setShowValueInput(false);
      }
    } else if (tempValue.trim()) {
      onAddValue(tempValue.trim());
      setTempValue("");
      setShowValueInput(false);
    }
  };

  const handleQuickAdd = (value: string) => {
    onAddValue(value);
  };

  const getOperatorLabel = (op: FilterOperator) => {
    return FILTER_OPERATORS.find((o) => o.value === op)?.label || op;
  };

  const getDataSourceLabel = (source: DataSourceEntity | undefined) => {
    if (!source) return "";
    switch (source) {
      case "FARM":
        return "Finca";
      case "FARM_ADMIN":
        return "Administrador";
      case "CHALLENGE":
        return "Inspección";
      case "CHALLENGE_FORM":
        return "Formulario";
      default:
        return source;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1,
        borderColor: "divider",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {fieldLabel}
            {dataSourceEntity && (
              <Chip
                label={getDataSourceLabel(dataSourceEntity)}
                size="small"
                variant="outlined"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  borderColor: "text.secondary",
                }}
              />
            )}
            {isAdvancedMode && (
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
                sx={{ ml: 1, fontWeight: 400 }}
              >
                | {getOperatorLabel(filter.operator)}
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {isAdvancedMode && (
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>Operador</InputLabel>
              <Select
                value={filter.operator}
                onChange={(e) => onUpdateOperator(e.target.value as FilterOperator)}
                label="Operador"
              >
                {FILTER_OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <IconButton size="small" onClick={onRemove} color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Values display */}
      {filter.operator === "BETWEEN" ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {filter.values.length >= 2 ? (
            <Chip
              label={`${filter.values[0]} and ${filter.values[1]}`}
              size="small"
              onDelete={() => {
                onRemoveValue(filter.values[0]);
                onRemoveValue(filter.values[1]);
              }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              {filter.values.length === 1
                ? `Desde: ${filter.values[0]} (agregar valor)`
                : "Agregar valores de rango"}
            </Typography>
          )}
          {filter.values.length < 2 && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {!showValueInput ? (
                <Chip
                  label="+ Agregar rango"
                  size="small"
                  clickable
                  onClick={() => setShowValueInput(true)}
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <TextField
                    size="small"
                    placeholder="Desde"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2">a</Typography>
                  <TextField
                    size="small"
                    placeholder="Hasta"
                    value={tempValue2}
                    onChange={(e) => setTempValue2(e.target.value)}
                    sx={{ minWidth: 80 }}
                  />
                  <Button size="small" onClick={handleAddValue}>
                    Agregar
                  </Button>
                  <IconButton size="small" onClick={() => setShowValueInput(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          {filter.values.map((value) => (
            <Chip key={value} label={value} size="small" onDelete={() => onRemoveValue(value)} />
          ))}
          {!showValueInput ? (
            <Chip
              label="+ Agregar valor"
              size="small"
              clickable
              onClick={() => setShowValueInput(true)}
              color="primary"
              variant="outlined"
            />
          ) : (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {enumValues && enumValues.length > 0 ? (
                <Select
                  size="small"
                  value={tempValue}
                  onChange={(e) => {
                    setTempValue(e.target.value);
                    if (e.target.value) {
                      handleQuickAdd(e.target.value);
                      setShowValueInput(false);
                    }
                  }}
                  sx={{ minWidth: 120 }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Seleccionar...</em>
                  </MenuItem>
                  {enumValues
                    .filter((v) => !filter.values.includes(v))
                    .map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                </Select>
              ) : (
                <>
                  <TextField
                    size="small"
                    placeholder="Ingresar valor"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddValue()}
                    sx={{ minWidth: 120 }}
                  />
                  <Button size="small" onClick={handleAddValue}>
                    Agregar
                  </Button>
                </>
              )}
              <IconButton size="small" onClick={() => setShowValueInput(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Show enum chips for quick add when available */}
      {enumValues && enumValues.length > 0 && filter.operator !== "BETWEEN" && !showValueInput && (
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {enumValues
            .filter((v) => !filter.values.includes(v))
            .slice(0, 8)
            .map((val) => (
              <Chip
                key={val}
                label={val}
                size="small"
                variant="outlined"
                clickable
                onClick={() => handleQuickAdd(val)}
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          {enumValues.filter((v) => !filter.values.includes(v)).length > 8 && (
            <Chip
              label={`+${enumValues.filter((v) => !filter.values.includes(v)).length - 8} más`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem" }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

// Add Filter Control Component
interface AddFilterControlProps {
  availableFields: Array<{
    key: string;
    label: string;
    type?: string;
    enumValues?: string[];
    dataSourceEntity?: DataSourceEntity;
    category?: string;
  }>;
  isAdvancedMode: boolean;
  onAddFilter: (fieldKey: string, operator: FilterOperator, values: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const AddFilterControl: React.FC<AddFilterControlProps> = ({
  availableFields,
  isAdvancedMode,
  onAddFilter,
  disabled = false,
  isLoading = false,
}) => {
  const [fieldKey, setFieldKey] = useState("");
  const [operator, setOperator] = useState<FilterOperator>("IN");
  const [values, setValues] = useState<string[]>([]);
  const [singleValue, setSingleValue] = useState("");
  const [betweenFrom, setBetweenFrom] = useState("");
  const [betweenTo, setBetweenTo] = useState("");

  const selectedField = availableFields.find((f) => f.key === fieldKey);
  const enumValues = selectedField?.enumValues;

  // Group fields by data source
  const fieldsBySource = availableFields.reduce(
    (acc, field) => {
      const source = field.dataSourceEntity || "OTHER";
      if (!acc[source]) {
        acc[source] = [];
      }
      acc[source].push(field);
      return acc;
    },
    {} as Record<string, typeof availableFields>
  );

  const handleAddFilter = () => {
    if (!fieldKey) return;

    let finalValues: string[] = [];
    let finalOperator = operator;

    if (isAdvancedMode) {
      if (operator === "IN") {
        finalValues = values;
      } else if (operator === "BETWEEN") {
        if (!betweenFrom || !betweenTo) return;
        finalValues = [betweenFrom, betweenTo];
      } else {
        if (!singleValue.trim()) return;
        finalValues = [singleValue.trim()];
      }
    } else {
      // Simple mode - always IN
      finalOperator = "IN";
      finalValues = values;
    }

    if (finalValues.length === 0) return;

    onAddFilter(fieldKey, finalOperator, finalValues);

    // Reset form
    setFieldKey("");
    setOperator("IN");
    setValues([]);
    setSingleValue("");
    setBetweenFrom("");
    setBetweenTo("");
  };

  const handleFieldChange = (newFieldKey: string) => {
    setFieldKey(newFieldKey);
    // Reset values when field changes
    setValues([]);
    setSingleValue("");
    setBetweenFrom("");
    setBetweenTo("");
  };

  const getDataSourceLabel = (source: DataSourceEntity) => {
    switch (source) {
      case "FARM":
        return "Finca";
      case "FARM_ADMIN":
        return "Administrador";
      case "CHALLENGE":
        return "Inspección";
      case "CHALLENGE_FORM":
        return "Campo de Formulario";
      default:
        return source;
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Campo de Filtro</InputLabel>
        <Select
          value={fieldKey}
          onChange={(e) => handleFieldChange(e.target.value)}
          label="Campo de Filtro"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <MenuItem disabled>
              <em>Cargando...</em>
            </MenuItem>
          ) : availableFields.length === 0 ? (
            <MenuItem disabled>
              <em>Sin campos disponibles</em>
            </MenuItem>
          ) : (
            Object.entries(fieldsBySource).flatMap(([source, fields]) => [
              // Header MenuItem
              ...(Object.keys(fieldsBySource).length > 1
                ? [
                    <MenuItem
                      key={`header-${source}`}
                      disabled
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        opacity: 1,
                      }}
                    >
                      {getDataSourceLabel(source as DataSourceEntity)}
                    </MenuItem>,
                  ]
                : []),
              // Field MenuItems
              ...fields.map((field) => (
                <MenuItem
                  key={field.key}
                  value={field.key}
                  sx={{ pl: field.dataSourceEntity ? 3 : 1 }}
                >
                  {field.label}
                </MenuItem>
              )),
            ])
          )}
        </Select>
      </FormControl>

      {isAdvancedMode && (
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Operador</InputLabel>
          <Select
            value={operator}
            onChange={(e) => setOperator(e.target.value as FilterOperator)}
            label="Operador"
            disabled={!fieldKey}
          >
            {FILTER_OPERATORS.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Value input based on mode and operator */}
      {!isAdvancedMode || operator === "IN" ? (
        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel>Valores</InputLabel>
          <Select
            multiple
            value={values}
            onChange={(e) => setValues(e.target.value as string[])}
            label="Valores"
            disabled={!fieldKey}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            )}
          >
            {enumValues && enumValues.length > 0 ? (
              enumValues.map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>Sin valores predefinidos</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      ) : operator === "BETWEEN" ? (
        <>
          <TextField
            size="small"
            label="Desde"
            value={betweenFrom}
            onChange={(e) => setBetweenFrom(e.target.value)}
            disabled={!fieldKey}
            sx={{ minWidth: 80 }}
          />
          <TextField
            size="small"
            label="Hasta"
            value={betweenTo}
            onChange={(e) => setBetweenTo(e.target.value)}
            disabled={!fieldKey}
            sx={{ minWidth: 80 }}
          />
        </>
      ) : (
        <TextField
          size="small"
          label="Valor"
          value={singleValue}
          onChange={(e) => setSingleValue(e.target.value)}
          disabled={!fieldKey}
          sx={{ minWidth: 120, flex: 1 }}
          placeholder={
            selectedField?.type === "number"
              ? "Ingresar número"
              : selectedField?.type === "date"
                ? "AAAA-MM-DD"
                : "Ingresar valor"
          }
        />
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={handleAddFilter}
        disabled={!fieldKey || (isAdvancedMode && operator === "IN" && values.length === 0)}
        startIcon={<AddIcon />}
      >
        Agregar Filtro
      </Button>
    </Box>
  );
};

export const DashboardCreator: React.FC<DashboardCreatorProps> = ({
  open,
  onClose,
  onSave,
  onDuplicate,
  dashboard,
  isLoading = false,
}) => {
  const { selectedOrganization } = useOrganization();

  // Dashboard basic info
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Scope filters
  const [scopeFilters, setScopeFilters] = useState<AnalyticsDashboardScopeFilterDto[]>([]);
  const [newScopeFilterType, setNewScopeFilterType] = useState<ScopeFilterType>("GROUPS");
  const [newScopeFilterValues, setNewScopeFilterValues] = useState<string[]>([]);

  // Graphs
  const [graphs, setGraphs] = useState<AnalyticsGraphDto[]>([]);
  const [editingGraph, setEditingGraph] = useState<AnalyticsGraphDto | null>(null);
  const [graphForm, setGraphForm] = useState<GraphFormData>(DEFAULT_GRAPH_FORM);
  const [showGraphForm, setShowGraphForm] = useState(false);

  // Graph filter form state (new simplified)
  const [isAdvancedFilterMode, setIsAdvancedFilterMode] = useState(false);
  const [newFilterField, setNewFilterField] = useState("");
  const [newFilterOperator, setNewFilterOperator] = useState<FilterOperator>("IN");
  const [newFilterValues, setNewFilterValues] = useState<string[]>([]);
  const [newFilterValue, setNewFilterValue] = useState(""); // For single value operators
  const [newFilterValue2, setNewFilterValue2] = useState(""); // For BETWEEN operator

  // CHALLENGE_FORM field selector modal state
  const [fieldSelectorOpen, setFieldSelectorOpen] = useState(false);
  const [selectedFieldInfo, setSelectedFieldInfo] = useState<ChallengeFormFieldSelection | null>(
    null
  );

  // Secondary dimension field selector modal state (for date fields and categorical fields)
  const [secondaryFieldSelectorOpen, setSecondaryFieldSelectorOpen] = useState(false);
  const [selectedSecondaryFieldInfo, setSelectedSecondaryFieldInfo] =
    useState<ChallengeFormFieldSelection | null>(null);

  // Map to store labels for form fields (key -> label) - persists across editing sessions
  const [fieldLabelsMap, setFieldLabelsMap] = useState<Map<string, string>>(new Map());

  // Map to store scope filter labels (filterType -> {uuid -> label})
  const [scopeFilterLabelsMap, setScopeFilterLabelsMap] = useState<
    Map<string, Map<string, string>>
  >(new Map());

  // Scope filter options
  const { data: scopeFilterOptions = [] } = useScopeFilterOptions(newScopeFilterType);

  // Dynamic fields for CHALLENGE_FORM data source
  const { data: dynamicFields = [], isLoading: _isLoadingFields } = useAvailableFields(
    graphForm.dataSourceEntity
  );

  // Initialize form with dashboard data
  useEffect(() => {
    if (dashboard) {
      setName(dashboard.name);
      setDescription(dashboard.description);
      setScopeFilters(dashboard.scopeFilters || []);
      setGraphs(dashboard.graphs || []);
    } else {
      setName("");
      setDescription("");
      setScopeFilters([]);
      setGraphs([]);
    }
    setShowGraphForm(false);
    setEditingGraph(null);
    setGraphForm(DEFAULT_GRAPH_FORM);
    setSelectedFieldInfo(null);
    // Clear the scope filter labels map when modal closes/reopens
    setScopeFilterLabelsMap(new Map());
  }, [dashboard, open]);

  // Update scope filter labels map when options are loaded
  useEffect(() => {
    if (scopeFilterOptions && scopeFilterOptions.length > 0) {
      setScopeFilterLabelsMap((prev) => {
        const newMap = new Map(prev);
        const typeLabels = new Map<string, string>();
        scopeFilterOptions.forEach((option: any) => {
          typeLabels.set(option.value, option.label);
        });
        newMap.set(newScopeFilterType, typeLabels);
        return newMap;
      });
    }
  }, [scopeFilterOptions, newScopeFilterType]);

  // Scroll graph form into view when opened
  useEffect(() => {
    if (showGraphForm) {
      const formElement = document.getElementById("graph-form");
      formElement?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showGraphForm]);

  const handleSave = () => {
    if (!name.trim()) {
      return;
    }

    // Convert graphs to create DTOs, preserving IDs for existing graphs
    const graphsToSave: (AnalyticsGraphCreateDto & { id?: string })[] = graphs.map((g, index) => ({
      ...(g.id && !g.id.startsWith("temp-") ? { id: g.id } : {}),
      name: g.name,
      description: g.description,
      graphType: g.graphType,
      dataSourceEntity: g.dataSourceEntity,
      dataSourceField: g.dataSourceField,
      aggregationType: g.aggregationType,
      // Secondary dimension
      secondaryDimensionType: g.secondaryDimensionType,
      secondaryDimensionField: g.secondaryDimensionField,
      secondaryDimensionGranularity: g.secondaryDimensionGranularity,
      // Field transform
      fieldTransform: g.fieldTransform,
      positionIndex: index,
      filters: g.filters,
    }));

    onSave({
      name: name.trim(),
      description: description.trim(),
      scopeFilters,
      graphs: graphsToSave,
    });
  };

  const handleAddScopeFilter = () => {
    if (newScopeFilterValues.length === 0) return;

    const newFilter: AnalyticsDashboardScopeFilterDto = {
      filterType: newScopeFilterType,
      filterValue: JSON.stringify(newScopeFilterValues),
    };

    // Remove existing filter of same type
    setScopeFilters((prev) => [
      ...prev.filter((f) => f.filterType !== newScopeFilterType),
      newFilter,
    ]);
    setNewScopeFilterValues([]);
  };

  const handleRemoveScopeFilter = (filterType: ScopeFilterType) => {
    setScopeFilters((prev) => prev.filter((f) => f.filterType !== filterType));
  };

  const getScopeFilterLabel = (filter: AnalyticsDashboardScopeFilterDto) => {
    const uuids = JSON.parse(filter.filterValue || "[]");
    const typeLabel = getScopeFilterTypeLabel(filter.filterType);

    // Get labels for UUIDs from the map
    const labelsMap = scopeFilterLabelsMap.get(filter.filterType);
    const displayValues = uuids.map((uuid: string) => {
      if (labelsMap?.has(uuid)) {
        return labelsMap.get(uuid);
      }
      return uuid; // Fallback to UUID if label not found
    });

    return `${typeLabel}: ${displayValues.length > 0 ? displayValues.join(", ") : "Ninguno"}`;
  };

  // Helper to get display label for a single scope filter UUID
  const getScopeFilterValueLabel = (filterType: ScopeFilterType, uuid: string): string => {
    const labelsMap = scopeFilterLabelsMap.get(filterType);
    return labelsMap?.get(uuid) || uuid;
  };

  // Graph filter handlers (new simplified)
  const handleAddGraphFilter = (fieldKey: string, operator: FilterOperator, values: string[]) => {
    if (!fieldKey) return;

    setGraphForm((prev) => {
      const existingFilterIndex = prev.filters.findIndex((f) => f.filterField === fieldKey);

      if (existingFilterIndex >= 0) {
        // Update existing filter
        const updatedFilters = [...prev.filters];
        updatedFilters[existingFilterIndex] = {
          filterField: fieldKey,
          operator,
          values,
        };
        return { ...prev, filters: updatedFilters };
      } else {
        // Add new filter
        return {
          ...prev,
          filters: [...prev.filters, { filterField: fieldKey, operator, values }],
        };
      }
    });

    // Reset filter form
    setNewFilterField("");
    setNewFilterOperator("IN");
    setNewFilterValues([]);
    setNewFilterValue("");
    setNewFilterValue2("");
  };

  const handleRemoveGraphFilter = (filterField: string) => {
    setGraphForm({
      ...graphForm,
      filters: graphForm.filters.filter((f) => f.filterField !== filterField),
    });
  };

  const handleAddFilterValue = (filterField: string, value: string) => {
    if (!value.trim()) return;

    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField ? { ...f, values: [...f.values, value.trim()] } : f
      ),
    }));
  };

  const handleRemoveFilterValue = (filterField: string, valueToRemove: string) => {
    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField
          ? { ...f, values: f.values.filter((v) => v !== valueToRemove) }
          : f
      ),
    }));
  };

  const handleUpdateFilterOperator = (filterField: string, newOperator: FilterOperator) => {
    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField
          ? { ...f, operator: newOperator, values: newOperator === "BETWEEN" ? ["", ""] : f.values }
          : f
      ),
    }));
  };

  const getGraphFilterLabel = (filter: AnalyticsGraphFilterDto) => {
    const field = getAllFilterableFields().find((f) => f.key === filter.filterField);
    const fieldLabel = field?.label || filter.filterField;
    const operatorLabel =
      FILTER_OPERATORS.find((o) => o.value === filter.filterOperator)?.label ||
      filter.filterOperator;

    let valueLabel = "";
    if (filter.filterOperator === "IN") {
      const values = JSON.parse(filter.filterValue || "[]");
      valueLabel = values.join(", ");
    } else if (filter.filterOperator === "BETWEEN") {
      valueLabel = `${filter.filterValue} and ${filter.filterValue2}`;
    } else {
      valueLabel = filter.filterValue || "";
    }

    return `${fieldLabel} ${operatorLabel} ${valueLabel}`;
  };

  const resetFilterForm = () => {
    setNewFilterField("");
    setNewFilterOperator("IN");
    setNewFilterValues([]);
    setNewFilterValue("");
    setNewFilterValue2("");
  };

  const handleAddGraph = () => {
    if (!graphForm.name.trim()) {
      return;
    }

    // Convert GraphFilterUI to AnalyticsGraphFilterDto for backend
    console.log("graphForm.filters:", graphForm.filters);
    const backendFilters = convertFiltersForBackend(graphForm.filters);
    console.log("backendFilters:", backendFilters);

    const newGraph: AnalyticsGraphCreateDto = {
      name: graphForm.name.trim(),
      description: graphForm.description.trim(),
      graphType: graphForm.graphType,
      dataSourceEntity: graphForm.dataSourceEntity,
      dataSourceField: graphForm.dataSourceField,
      aggregationType: graphForm.aggregationType,
      // MAP: secondaryDimensionField is used for transparency (no dimension type)
      secondaryDimensionType:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.secondaryDimensionType !== "NONE"
            ? graphForm.secondaryDimensionType
            : undefined,
      secondaryDimensionField:
        graphForm.graphType === "MAP"
          ? graphForm.secondaryDimensionField
          : graphForm.secondaryDimensionType !== "NONE"
            ? graphForm.secondaryDimensionField
            : undefined,
      secondaryDimensionGranularity:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.secondaryDimensionType === "TIME"
            ? graphForm.secondaryDimensionGranularity
            : undefined,
      // Field transform - not used for MAP
      fieldTransform:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.fieldTransform !== "NONE"
            ? graphForm.fieldTransform
            : undefined,
      positionIndex: graphs.length,
      filters: backendFilters,
    };

    // If editing, update; otherwise add new
    if (editingGraph) {
      setGraphs((prev: AnalyticsGraphDto[]) =>
        prev.map((g: AnalyticsGraphDto) =>
          g.id === editingGraph.id
            ? {
                ...g,
                name: newGraph.name || "",
                description: newGraph.description || "",
                graphType: newGraph.graphType,
                dataSourceEntity: newGraph.dataSourceEntity,
                dataSourceField: newGraph.dataSourceField,
                aggregationType: newGraph.aggregationType,
                secondaryDimensionType: newGraph.secondaryDimensionType,
                secondaryDimensionField: newGraph.secondaryDimensionField,
                secondaryDimensionGranularity: newGraph.secondaryDimensionGranularity,
                fieldTransform: newGraph.fieldTransform,
                filters: backendFilters,
                id: g.id,
                dashboardId: g.dashboardId,
              }
            : g
        )
      );
    } else {
      // This is a temp graph - will get real ID on save
      const tempGraph: AnalyticsGraphDto = {
        id: `temp-${Date.now()}`,
        dashboardId: dashboard?.id || "",
        name: newGraph.name || "",
        description: newGraph.description || "",
        graphType: newGraph.graphType,
        dataSourceEntity: newGraph.dataSourceEntity,
        dataSourceField: newGraph.dataSourceField,
        aggregationType: newGraph.aggregationType,
        secondaryDimensionType: newGraph.secondaryDimensionType,
        secondaryDimensionField: newGraph.secondaryDimensionField,
        secondaryDimensionGranularity: newGraph.secondaryDimensionGranularity,
        fieldTransform: newGraph.fieldTransform,
        positionIndex: graphs.length,
        filters: newGraph.filters || [],
      };
      console.log("tempGraph:", tempGraph);
      setGraphs((prev: AnalyticsGraphDto[]) => [...prev, tempGraph]);
    }

    setShowGraphForm(false);
    setEditingGraph(null);
    setGraphForm(DEFAULT_GRAPH_FORM);
    setSelectedSecondaryFieldInfo(null);
  };

  const handleEditGraph = (graph: AnalyticsGraphDto) => {
    setEditingGraph(graph);
    // Convert backend filters to UI format
    const uiFilters = convertFiltersFromBackend((graph.filters || []) || []);
    setGraphForm({
      name: graph.name || "",
      description: graph.description || "",
      graphType: graph.graphType,
      dataSourceEntity: graph.dataSourceEntity,
      dataSourceField: graph.dataSourceField,
      aggregationType: graph.aggregationType,
      // Secondary dimension
      secondaryDimensionType: (graph.secondaryDimensionType as SecondaryDimensionType) || "NONE",
      secondaryDimensionField: graph.secondaryDimensionField || "",
      secondaryDimensionGranularity:
        (graph.secondaryDimensionGranularity as DateGranularity) || "MONTH",
      // Field transform
      fieldTransform: (graph.fieldTransform as FieldTransform) || "NONE",
      filters: uiFilters,
    });

    // If editing a CHALLENGE_FORM graph, try to parse the field info
    if (graph.dataSourceEntity === "CHALLENGE_FORM") {
      // Parse the field key using the utility function
      const parsedField = parseFieldKey(graph.dataSourceField);

      // Try to get the label from dynamic fields or stored map
      const dynamicField = dynamicFields.find((f) => f.key === graph.dataSourceField);
      const storedLabel = fieldLabelsMap.get(graph.dataSourceField);
      const label = dynamicField?.label || storedLabel || graph.dataSourceField;

      if (parsedField) {
        const fieldInfo: ChallengeFormFieldSelection = {
          key: graph.dataSourceField,
          label: label,
          type: dynamicField?.type || "string",
          programName: "Program",
          versionName: "Version",
          itemName: "Item",
          fieldName: "Field",
          questionId: parsedField.questionId,
          itemId: (parsedField as any).itemId,
          versionId: (parsedField as any).versionId,
          programId: (parsedField as any).programId,
          forEachContext: parsedField.forEachValues,
        };
        setSelectedFieldInfo(fieldInfo);
        // Store in map for future use
        setFieldLabelsMap((prev) => new Map(prev).set(graph.dataSourceField, label));
      } else {
        // For legacy UUID-only keys, just store the key
        setSelectedFieldInfo({
          key: graph.dataSourceField,
          label: label,
          type: "string",
          programName: "Unknown",
          versionName: "Unknown",
          itemName: "Unknown",
          fieldName: "Unknown",
          questionId: "",
          itemId: graph.dataSourceField,
          versionId: "",
          programId: "",
        });
      }
    } else {
      setSelectedFieldInfo(null);
    }

    // Populate secondary dimension field info if exists
    if (graph.secondaryDimensionField) {
      const storedSecondaryLabel = fieldLabelsMap.get(graph.secondaryDimensionField);
      const secondaryDynamicField = dynamicFields.find(
        (f) => f.key === graph.secondaryDimensionField
      );
      const secondaryLabel = secondaryDynamicField?.label || storedSecondaryLabel;

      if (secondaryLabel) {
        setSelectedSecondaryFieldInfo({
          key: graph.secondaryDimensionField,
          label: secondaryLabel,
          type: secondaryDynamicField?.type || "string",
          questionId: graph.secondaryDimensionField,
        });
        // Store in map
        setFieldLabelsMap((prev) =>
          new Map(prev).set(graph.secondaryDimensionField || '', secondaryLabel)
        );
      } else {
        // Set a placeholder that shows we have a value
        setSelectedSecondaryFieldInfo({
          key: graph.secondaryDimensionField,
          label: `Campo (${graph.secondaryDimensionField.substring(0, 8)}...)`,
          type: "string",
          questionId: graph.secondaryDimensionField,
        });
      }
    } else {
      setSelectedSecondaryFieldInfo(null);
    }

    setShowGraphForm(true);
  };

  const handleDeleteGraph = (graphId: string) => {
    setGraphs((prev: AnalyticsGraphDto[]) => prev.filter((g: AnalyticsGraphDto) => g.id !== graphId));
  };

  const handleDuplicateGraph = (graph: AnalyticsGraphDto) => {
    const duplicated: AnalyticsGraphDto = {
      ...graph,
      id: `temp-${Date.now()}`,
      name: `${graph.name} (copy)`,
      positionIndex: graphs.length,
    };
    setGraphs((prev: any) => [...prev, duplicated]);
  };

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start dragging (prevents accidental drags)
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setGraphs((items) => {
        const oldIndex = items.findIndex((g) => g.id === active.id);
        const newIndex = items.findIndex((g) => g.id === over.id);

        // Create reordered array
        const reordered = [...items];
        const [movedItem] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, movedItem);

        // Update positionIndex for each graph
        return reordered.map((g, i) => ({ ...g, positionIndex: i }));
      });
    }
  };

  const cancelGraphForm = () => {
    setShowGraphForm(false);
    setEditingGraph(null);
    setGraphForm(DEFAULT_GRAPH_FORM);
    setSelectedFieldInfo(null);
    resetFilterForm();
  };

  const handleChallengeFormFieldSelected = (field: ChallengeFormFieldSelection) => {
    setSelectedFieldInfo(field);
    setGraphForm({
      ...graphForm,
      dataSourceField: field.key,
    });
    // Store the label in the map for later display
    setFieldLabelsMap((prev) => new Map(prev).set(field.key, field.label));
    setFieldSelectorOpen(false);
  };

  const handleSecondaryFieldSelected = (field: ChallengeFormFieldSelection) => {
    setSelectedSecondaryFieldInfo(field);
    setGraphForm({
      ...graphForm,
      secondaryDimensionField: field.key,
    });
    // Store the label in the map for later display
    setFieldLabelsMap((prev) => new Map(prev).set(field.key, field.label));
    setSecondaryFieldSelectorOpen(false);
  };

  const getAvailableFields = () => {
    // For CHALLENGE_FORM, use dynamically fetched fields from backend
    if (graphForm.dataSourceEntity === "CHALLENGE_FORM") {
      return dynamicFields.length > 0 ? dynamicFields : [];
    }
    return PREDEFINED_FIELDS[graphForm.dataSourceEntity] || [];
  };

  // Get all filterable fields from all data sources (for graph filters)
  // Graph filters can filter by fields from any data source, similar to scope filters
  const getAllFilterableFields = () => {
    const allFields: Array<{
      key: string;
      label: string;
      type?: string;
      enumValues?: string[];
      category?: string;
      dataSourceEntity?: DataSourceEntity;
    }> = [];

    // Add FARM fields
    PREDEFINED_FIELDS.FARM.forEach((field) => {
      allFields.push({ ...field, dataSourceEntity: "FARM" });
    });

    // Add FARM_ADMIN fields
    PREDEFINED_FIELDS.FARM_ADMIN.forEach((field) => {
      allFields.push({ ...field, dataSourceEntity: "FARM_ADMIN" });
    });

    // Add CHALLENGE fields
    PREDEFINED_FIELDS.CHALLENGE.forEach((field) => {
      allFields.push({ ...field, dataSourceEntity: "CHALLENGE" });
    });

    // Add CHALLENGE_FORM dynamic fields if available
    dynamicFields.forEach((field) => {
      allFields.push({ ...field, dataSourceEntity: "CHALLENGE_FORM" });
    });

    return allFields;
  };

  // Get field by key for filter cards
  const getFieldByKey = (fieldKey: string) => {
    // First check if we have a stored label from form selection
    const storedLabel = fieldLabelsMap.get(fieldKey);
    if (storedLabel) {
      const predefinedField = getAllFilterableFields().find((f) => f.key === fieldKey);
      return {
        key: fieldKey,
        label: storedLabel,
        type: predefinedField?.type,
        enumValues: predefinedField?.enumValues,
        dataSourceEntity: predefinedField?.dataSourceEntity,
      };
    }
    return getAllFilterableFields().find((f) => f.key === fieldKey);
  };

  // Helper function to get display label for any field key
  const getDisplayLabelForKey = (fieldKey: string, dataSourceEntity?: DataSourceEntity): string => {
    // First check stored labels from form selection
    const storedLabel = fieldLabelsMap.get(fieldKey);
    if (storedLabel) {
      return storedLabel;
    }

    // Check predefined fields
    if (dataSourceEntity) {
      const predefinedField = PREDEFINED_FIELDS[dataSourceEntity]?.find((f) => f.key === fieldKey);
      if (predefinedField) {
        return predefinedField.label;
      }
    }

    // Check dynamic fields
    const dynamicField = dynamicFields.find((f) => f.key === fieldKey);
    if (dynamicField) {
      return dynamicField.label;
    }

    // Fallback to key itself (truncated if UUID)
    if (fieldKey.includes("-") && fieldKey.length > 30) {
      return `Campo (${fieldKey.substring(0, 8)}...)`;
    }
    return fieldKey;
  };

  // Helper function to get display label for a graph's field
  const getFieldDisplayLabel = (graph: AnalyticsGraphDto): string => {
    // First check stored labels from form selection
    const storedLabel = fieldLabelsMap.get(graph.dataSourceField);
    if (storedLabel) {
      const parsedField = parseFieldKey(graph.dataSourceField);
      if (parsedField?.forEachValues && parsedField.forEachValues.length > 0) {
        return `${storedLabel}\nFOR_EACH: ${parsedField.forEachValues.join(", ")}`;
      }
      return storedLabel;
    }

    if (graph.dataSourceEntity === "CHALLENGE_FORM") {
      // Check dynamic fields
      const dynamicField = dynamicFields.find((f) => f.key === graph.dataSourceField);
      if (dynamicField) {
        const parsedField = parseFieldKey(graph.dataSourceField);
        if (parsedField?.forEachValues && parsedField.forEachValues.length > 0) {
          return `${dynamicField.label}\nFOR_EACH: ${parsedField.forEachValues.join(", ")}`;
        }
        return dynamicField.label;
      }

      // Fallback - show truncated UUID
      const parsedField = parseFieldKey(graph.dataSourceField);
      if (parsedField) {
        const truncated = parsedField.questionId.substring(0, 20);
        return `Campo (${truncated}${parsedField.questionId.length > 20 ? "..." : ""})`;
      }
    }
    // Fallback to predefined fields or show raw value
    return (
      PREDEFINED_FIELDS[graph.dataSourceEntity]?.find((f) => f.key === graph.dataSourceField)
        ?.label || graph.dataSourceField
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: "var(--tertiary)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 32px",
          background: "linear-gradient(180deg, var(--tertiary) 0%, var(--background-paper) 100%)",
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "var(--text-primary)",
          }}
        >
          {dashboard ? "Editar Dashboard" : "Crear Dashboard"}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "var(--text-secondary)",
            "&:hover": {
              color: "var(--primary)",
              backgroundColor: "var(--tertiary)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          padding: "24px 32px",
          backgroundColor: "var(--tertiary)",
          borderColor: "var(--border-color)",
        }}
      >
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Paper
              sx={{
                padding: "24px",
                borderRadius: "var(--card-border-radius)",
                border: "1px solid var(--border-color)",
                backgroundColor: "var(--background-paper)",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Información del Dashboard
              </Typography>
              <TextField
                fullWidth
                label="Nombre del Dashboard"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mi Dashboard de Análisis"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--input-border-radius)",
                  },
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descripción"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe qué muestra este dashboard..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--input-border-radius)",
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Scope Filters */}
          <Grid item xs={12}>
            <Paper
              sx={{
                padding: "24px",
                borderRadius: "var(--card-border-radius)",
                border: "1px solid var(--border-color)",
                backgroundColor: "var(--background-paper)",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Filtros de Alcance
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ml: 1,
                    fontWeight: 400,
                    color: "var(--text-secondary)",
                  }}
                >
                  (Aplicar a todos los gráficos)
                </Typography>
              </Typography>

              {/* Current Scope Filters */}
              {scopeFilters.length > 0 && (
                <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {scopeFilters.map((filter) => (
                    <Chip
                      key={filter.filterType}
                      label={getScopeFilterLabel(filter)}
                      onDelete={() => handleRemoveScopeFilter(filter.filterType)}
                      size="medium"
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "var(--secondary)",
                        color: "var(--text-primary)",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Add Scope Filter */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Tipo de Filtro</InputLabel>
                  <Select
                    value={newScopeFilterType}
                    onChange={(e) => setNewScopeFilterType(e.target.value as ScopeFilterType)}
                    label="Tipo de Filtro"
                    sx={{
                      borderRadius: "var(--input-border-radius)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                    }}
                  >
                    {SCOPE_FILTER_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 250, flex: 1 }}>
                  <InputLabel>Seleccionar Valores</InputLabel>
                  <Select
                    multiple
                    value={newScopeFilterValues}
                    onChange={(e) => setNewScopeFilterValues(e.target.value as string[])}
                    label="Seleccionar Valores"
                    sx={{
                      borderRadius: "var(--input-border-radius)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={getScopeFilterValueLabel(newScopeFilterType, value)}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {scopeFilterOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleAddScopeFilter}
                  disabled={newScopeFilterValues.length === 0}
                  startIcon={<AddIcon />}
                  sx={{
                    fontWeight: 500,
                    borderRadius: "var(--button-border-radius)",
                    borderColor: "var(--border-color)",
                    color: "var(--text-primary)",
                    "&:hover": {
                      backgroundColor: "var(--tertiary)",
                      borderColor: "var(--primary)",
                    },
                  }}
                >
                  Agregar Filtro
                </Button>
              </Box>

              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  borderRadius: "var(--input-border-radius)",
                  backgroundColor: "var(--tertiary-light)",
                  border: "1px solid var(--border-color)",
                }}
              >
                Los filtros de alcance aplican a TODOS los gráficos de este dashboard. Úsalos para
                filtrar la población de fincas (ej: grupos específicos, ciudades o regiones).
              </Alert>
            </Paper>
          </Grid>

          {/* Graphs */}
          <Grid item xs={12}>
            <Paper
              sx={{
                padding: "24px",
                borderRadius: "var(--card-border-radius)",
                border: "1px solid var(--border-color)",
                backgroundColor: "var(--background-paper)",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  Gráficos ({graphs.length})
                </Typography>
                {!showGraphForm && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowGraphForm(true)}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      borderRadius: "var(--button-border-radius)",
                      backgroundColor: "var(--primary)",
                      "&:hover": {
                        backgroundColor: "#1a2244",
                      },
                    }}
                  >
                    Agregar Gráfico
                  </Button>
                )}
              </Box>

              {/* Graph Form */}
              {showGraphForm && (
                <Paper
                  id="graph-form"
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: "var(--tertiary)",
                    borderRadius: "var(--card-border-radius)",
                    border: "1px solid var(--border-color)",
                  }}
                  variant="outlined"
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {editingGraph ? "Editar Gráfico" : "Nuevo Gráfico"}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre del Gráfico"
                        value={graphForm.name}
                        onChange={(e) => setGraphForm({ ...graphForm, name: e.target.value })}
                        placeholder="Cantidad de Fincas por Región"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo de Gráfico</InputLabel>
                        <Select
                          value={graphForm.graphType}
                          onChange={(e) => {
                            const newType = e.target.value as GraphType;
                            if (newType === "MAP") {
                              setGraphForm({
                                ...graphForm,
                                graphType: newType,
                                dataSourceEntity: "CHALLENGE_FORM",
                                aggregationType: "COUNT",
                                secondaryDimensionType: "NONE",
                                fieldTransform: "NONE",
                              });
                            } else {
                              setGraphForm({ ...graphForm, graphType: newType });
                            }
                          }}
                          label="Tipo de Gráfico"
                        >
                          {GRAPH_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              <Box>
                                <Typography variant="body2">{type.label}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {type.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={graphForm.graphType === "MAP"}>
                        <InputLabel>Data Source</InputLabel>
                        <Select
                          value={graphForm.dataSourceEntity}
                          onChange={(e) => {
                            const entity = e.target.value as DataSourceEntity;
                            const fields = PREDEFINED_FIELDS[entity];
                            setGraphForm({
                              ...graphForm,
                              dataSourceEntity: entity,
                              dataSourceField: fields?.[0]?.key || "",
                              filters: [], // Clear filters when changing data source
                              // Reset secondary dimension and transform on data source change
                              secondaryDimensionType: "NONE",
                              secondaryDimensionField: "",
                              secondaryDimensionGranularity: "MONTH",
                              fieldTransform: "NONE",
                            });
                            setSelectedFieldInfo(null); // Clear CHALLENGE_FORM field selection
                            setSelectedSecondaryFieldInfo(null); // Clear secondary field selection
                            resetFilterForm();
                          }}
                          label="Fuente de Datos"
                        >
                          {(Object.keys(DATA_SOURCE_LABELS) as DataSourceEntity[]).map((entity) => (
                            <MenuItem key={entity} value={entity}>
                              {DATA_SOURCE_LABELS[entity]}
                              {entity === "CHALLENGE_FORM" && " (Dinámico)"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {graphForm.dataSourceEntity === "CHALLENGE_FORM" ? (
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={() => setFieldSelectorOpen(true)}
                            startIcon={<FolderOpenIcon />}
                            fullWidth
                            sx={{ height: 56 }}
                          >
                            {selectedFieldInfo?.label ||
                              (graphForm.dataSourceField
                                ? getDisplayLabelForKey(
                                    graphForm.dataSourceField,
                                    graphForm.dataSourceEntity
                                  )
                                : graphForm.graphType === "MAP"
                                  ? "Seleccionar Campo de Color..."
                                  : "Seleccionar Campo del Formulario...")}
                          </Button>
                          {selectedFieldInfo && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ mt: 0.5, ml: 1, display: "block" }}
                            >
                              {selectedFieldInfo.label}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <FormControl fullWidth>
                          <InputLabel>Campo para Agrupar</InputLabel>
                          <Select
                            value={graphForm.dataSourceField}
                            onChange={(e) =>
                              setGraphForm({ ...graphForm, dataSourceField: e.target.value })
                            }
                            label="Campo para Agrupar"
                          >
                            {getAvailableFields().length === 0 ? (
                              <MenuItem disabled>
                                <em>Sin campos disponibles</em>
                              </MenuItem>
                            ) : (
                              getAvailableFields().map((field) => (
                                <MenuItem key={field.key} value={field.key}>
                                  {field.label}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </Grid>

                    {graphForm.graphType !== "MAP" && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Agregación</InputLabel>
                          <Select
                            value={graphForm.aggregationType}
                            onChange={(e) =>
                              setGraphForm({
                                ...graphForm,
                                aggregationType: e.target.value as AggregationType,
                              })
                            }
                            label="Agregación"
                          >
                            {AGGREGATION_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {/* MAP: Transparency Field Selector */}
                    {graphForm.graphType === "MAP" && (
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          onClick={() => setSecondaryFieldSelectorOpen(true)}
                          startIcon={<FolderOpenIcon />}
                          fullWidth
                          sx={{ height: 56 }}
                        >
                          {selectedSecondaryFieldInfo?.label ||
                            (graphForm.secondaryDimensionField
                              ? getDisplayLabelForKey(
                                  graphForm.secondaryDimensionField,
                                  graphForm.dataSourceEntity
                                )
                              : "Seleccionar Campo de Transparencia (Opcional)...")}
                        </Button>
                      </Grid>
                    )}

                    {/* Field Transform Section - Hidden for MAP */}
                    {graphForm.graphType !== "MAP" && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Transformación de Campo
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Transformar</InputLabel>
                          <Select
                            value={graphForm.fieldTransform}
                            onChange={(e) =>
                              setGraphForm({
                                ...graphForm,
                                fieldTransform: e.target.value as FieldTransform,
                              })
                            }
                            label="Transformar"
                          >
                            {FIELD_TRANSFORMS.map((transform) => (
                              <MenuItem key={transform.value} value={transform.value}>
                                <Box>
                                  <Typography variant="body2">{transform.label}</Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {transform.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Secondary Dimension Section - Hidden for MAP */}
                    {graphForm.graphType !== "MAP" && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">Dimensión Secundaria</Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Tipo de Dimensión</InputLabel>
                              <Select
                                value={graphForm.secondaryDimensionType}
                                onChange={(e) => {
                                  const newType = e.target.value as SecondaryDimensionType;
                                  setGraphForm({
                                    ...graphForm,
                                    secondaryDimensionType: newType,
                                    secondaryDimensionField:
                                      newType === "NONE" ? "" : graphForm.secondaryDimensionField,
                                  });
                                  if (newType === "NONE") {
                                    setSelectedSecondaryFieldInfo(null);
                                  }
                                }}
                                label="Tipo de Dimensión"
                              >
                                {SECONDARY_DIMENSION_TYPES.map((dimType) => (
                                  <MenuItem key={dimType.value} value={dimType.value}>
                                    <Box>
                                      <Typography variant="body2">{dimType.label}</Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {dimType.description}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {graphForm.secondaryDimensionType !== "NONE" && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ mb: 0.5, display: "block" }}
                                >
                                  Selección Rápida
                                </Typography>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Campo</InputLabel>
                                  <Select
                                    value={(() => {
                                      // Strip data source prefix for comparison with predefined fields
                                      const fieldKey = graphForm.secondaryDimensionField?.includes(
                                        "."
                                      )
                                        ? graphForm.secondaryDimensionField.split(".", 2)[1]
                                        : graphForm.secondaryDimensionField;
                                      const allPredefinedFields = [
                                        ...(DATE_FIELDS_BY_SOURCE[graphForm.dataSourceEntity] ||
                                          []),
                                        ...(graphForm.secondaryDimensionType === "CATEGORICAL"
                                          ? PREDEFINED_FIELDS[graphForm.dataSourceEntity]?.filter(
                                              (f) => f.type === "string"
                                            ) || []
                                          : []),
                                      ];
                                      return allPredefinedFields.some((f) => f.key === fieldKey)
                                        ? fieldKey
                                        : "";
                                    })()}
                                    onChange={(e) => {
                                      // Prefix predefined fields with data source (e.g., "FARM.region")
                                      // Form questions (UUIDs) are saved without prefix
                                      const field = e.target.value;
                                      const isFormQuestion = field.match(
                                        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
                                      );
                                      const prefixedField = isFormQuestion
                                        ? field
                                        : `${graphForm.dataSourceEntity}.${field}`;
                                      setGraphForm({
                                        ...graphForm,
                                        secondaryDimensionField: prefixedField,
                                      });
                                      setSelectedSecondaryFieldInfo(null);
                                    }}
                                    label="Campo"
                                  >
                                    <MenuItem value="">
                                      <em>Seleccionar campo predefinido...</em>
                                    </MenuItem>
                                    {(DATE_FIELDS_BY_SOURCE[graphForm.dataSourceEntity] || []).map(
                                      (field) => (
                                        <MenuItem key={field.key} value={field.key}>
                                          {field.label}
                                        </MenuItem>
                                      )
                                    )}
                                    {/* Add categorical fields for CATEGORICAL type */}
                                    {graphForm.secondaryDimensionType === "CATEGORICAL" && (
                                      <>
                                        {PREDEFINED_FIELDS[graphForm.dataSourceEntity]
                                          ?.filter((f) => f.type === "string")
                                          .map((field) => (
                                            <MenuItem key={field.key} value={field.key}>
                                              {field.label}
                                            </MenuItem>
                                          ))}
                                      </>
                                    )}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ mb: 0.5, display: "block" }}
                                >
                                  O Seleccionar desde Formularios
                                </Typography>
                                <Button
                                  variant="outlined"
                                  onClick={() => setSecondaryFieldSelectorOpen(true)}
                                  startIcon={<FolderOpenIcon />}
                                  fullWidth
                                  sx={{ height: 40 }}
                                >
                                  {selectedSecondaryFieldInfo?.label ||
                                    (graphForm.secondaryDimensionField
                                      ? getDisplayLabelForKey(graphForm.secondaryDimensionField)
                                      : "Seleccionar Campo...")}
                                </Button>
                              </Grid>
                              {graphForm.secondaryDimensionType === "TIME" && (
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Granularidad</InputLabel>
                                    <Select
                                      value={graphForm.secondaryDimensionGranularity}
                                      onChange={(e) =>
                                        setGraphForm({
                                          ...graphForm,
                                          secondaryDimensionGranularity: e.target
                                            .value as DateGranularity,
                                        })
                                      }
                                      label="Granularidad"
                                    >
                                      {DATE_GRANULARITIES.map((granularity) => (
                                        <MenuItem key={granularity.value} value={granularity.value}>
                                          {granularity.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    )}

                    {/* Graph Filters Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 2,
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Filtros del Gráfico
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ ml: 1, fontWeight: 400 }}
                          >
                            (Aplicar solo a este gráfico)
                          </Typography>
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isAdvancedFilterMode}
                              onChange={(e) => setIsAdvancedFilterMode(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Avanzado"
                        />
                      </Box>

                      {/* Filter Cards */}
                      {graphForm.filters.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          {graphForm.filters.map((filter) => {
                            const field = getFieldByKey(filter.filterField);
                            return (
                              <FilterCard
                                key={filter.filterField}
                                filter={filter}
                                fieldLabel={field?.label || filter.filterField}
                                fieldType={field?.type}
                                enumValues={field?.enumValues}
                                dataSourceEntity={field?.dataSourceEntity}
                                isAdvancedMode={isAdvancedFilterMode}
                                availableFields={getAllFilterableFields()}
                                onUpdateOperator={(op) =>
                                  handleUpdateFilterOperator(filter.filterField, op)
                                }
                                onAddValue={(val) => handleAddFilterValue(filter.filterField, val)}
                                onRemoveValue={(val) =>
                                  handleRemoveFilterValue(filter.filterField, val)
                                }
                                onRemove={() => handleRemoveGraphFilter(filter.filterField)}
                              />
                            );
                          })}
                        </Box>
                      )}

                      {/* Add Filter Control */}
                      <AddFilterControl
                        availableFields={getAllFilterableFields()}
                        isAdvancedMode={isAdvancedFilterMode}
                        onAddFilter={handleAddGraphFilter}
                        isLoading={false}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                          onClick={cancelGraphForm}
                          sx={{
                            fontWeight: 500,
                            borderRadius: "var(--button-border-radius)",
                            color: "var(--text-primary)",
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddGraph}
                          disabled={!graphForm.name.trim()}
                          sx={{
                            fontWeight: 600,
                            borderRadius: "var(--button-border-radius)",
                            backgroundColor: "var(--primary)",
                            "&:hover": {
                              backgroundColor: "#1a2244",
                            },
                            "&:disabled": {
                              backgroundColor: "var(--text-disabled)",
                            },
                          }}
                        >
                          {editingGraph ? "Actualizar Gráfico" : "Agregar Gráfico"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Graph List */}
              {graphs.length === 0 && !showGraphForm && (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "var(--tertiary)",
                    borderRadius: "var(--card-border-radius)",
                    border: "1px dashed var(--border-color)",
                  }}
                >
                  <Typography sx={{ color: "var(--text-secondary)" }}>
                    Sin gráficos aún. Agrega un gráfico para mostrar datos en tu dashboard.
                  </Typography>
                </Paper>
              )}

              {/* Graph List with Drag-and-Drop Reordering */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={graphs.map((g) => g.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                    {graphs.map((graph, index) => (
                      <SortableGraphItem
                        key={graph.id}
                        graph={graph}
                        index={index}
                        onEdit={handleEditGraph}
                        onDuplicate={handleDuplicateGraph}
                        onDelete={handleDeleteGraph}
                        getFieldDisplayLabel={getFieldDisplayLabel}
                        getGraphFilterLabel={getGraphFilterLabel}
                        convertFiltersFromBackend={convertFiltersFromBackend}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 32px",
          borderTop: "1px solid var(--border-color)",
          backgroundColor: "var(--background-paper)",
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            fontWeight: 500,
            borderRadius: "var(--button-border-radius)",
            color: "var(--text-primary)",
          }}
        >
          Cancelar
        </Button>
        {dashboard && onDuplicate && (
          <Button
            onClick={() => onDuplicate(dashboard.id)}
            disabled={isLoading}
            startIcon={<ContentCopyIcon />}
            sx={{
              fontWeight: 500,
              borderRadius: "var(--button-border-radius)",
              color: "var(--text-primary)",
              borderColor: "var(--border-color)",
              "&:hover": {
                backgroundColor: "var(--tertiary)",
                borderColor: "var(--primary)",
              },
            }}
          >
            Duplicar Dashboard
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
          sx={{
            fontWeight: 600,
            borderRadius: "var(--button-border-radius)",
            backgroundColor: "var(--primary)",
            "&:hover": {
              backgroundColor: "#1a2244",
            },
            "&:disabled": {
              backgroundColor: "var(--text-disabled)",
            },
          }}
        >
          {dashboard ? "Guardar Cambios" : "Crear Dashboard"}
        </Button>
      </DialogActions>

      {/* CHALLENGE_FORM Field Selector Modal */}
      {selectedOrganization?.id && (
        <ChallengeFormFieldSelectorModal
          open={fieldSelectorOpen}
          onClose={() => setFieldSelectorOpen(false)}
          onFieldSelected={handleChallengeFormFieldSelected}
          organizationId={selectedOrganization.id}
          existingFieldKey={
            graphForm.dataSourceEntity === "CHALLENGE_FORM" ? graphForm.dataSourceField : undefined
          }
          filterByType={graphForm.graphType === "MAP" ? ["enum"] : undefined}
          title={graphForm.graphType === "MAP" ? "Seleccionar Campo de Color" : undefined}
        />
      )}

      {/* Secondary Dimension Field Selector Modal */}
      {selectedOrganization?.id && (
        <ChallengeFormFieldSelectorModal
          open={secondaryFieldSelectorOpen}
          onClose={() => setSecondaryFieldSelectorOpen(false)}
          onFieldSelected={handleSecondaryFieldSelected}
          organizationId={selectedOrganization.id}
          title={
            graphForm.graphType === "MAP"
              ? "Seleccionar Campo de Transparencia"
              : "Seleccionar Campo para Dimensión Secundaria"
          }
          filterByType={
            graphForm.graphType === "MAP"
              ? ["number", "date"]
              : graphForm.secondaryDimensionType === "TIME"
                ? ["date", "datetime"]
                : undefined
          }
        />
      )}
    </Dialog>
  );
};
